import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { ApolloError, useLazyQuery } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import { UserActionTypes } from '../redux/user/types/UserActionTypes'
import { checkSignInRoles } from '../utils/checkSignInRole'
import { ILoginData, ILoginVars, LOGIN_QUERY } from '../graphql/auth/login'
import LogoCircle from '../components/Shared/LogoCircle'
import AppVersionLabel from '../components/Shared/AppVersionLabel'
import { colors } from '../const/colors'
import { alertTimestamp } from '../utils/alertTimestamp'

type SignInFormData = {
  login: string
  password: string
}

const defaultValues = {
  login: '',
  password: '',
}

interface ICustomWindow extends Window {
  TAG?: string
}
declare var window: ICustomWindow

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '13px',
    backgroundColor: colors.surface,
    fontSize: '14.5px',
    color: colors.textBody,
    '& fieldset': {
      borderColor: colors.border3,
      transition: 'border-color .15s ease',
    },
    '&:hover fieldset': { borderColor: colors.slate },
    '&.Mui-focused fieldset': {
      borderColor: colors.navy,
      borderWidth: '1.5px',
    },
    '&.Mui-error fieldset': { borderColor: colors.red },
  },
  '& .MuiOutlinedInput-input': { padding: '14px 16px' },
}

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    component="label"
    sx={{
      display: 'block',
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: colors.textMuted,
      marginBottom: '8px',
    }}
  >
    {children}
  </Typography>
)

const HeroFeature = ({ label }: { label: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        background: 'rgba(143,212,155,0.16)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <CheckIcon sx={{ fontSize: 13, color: colors.greenMint }} />
    </Box>
    <Typography sx={{ fontSize: '13.5px', color: 'rgba(255,255,255,0.78)' }}>
      {label}
    </Typography>
  </Box>
)

const Login = ({ prevRoute }: { prevRoute: string }) => {
  const { t } = useTranslation(['form', 'btn', 'login_screen', 'code'])
  const [errorMessage, setErrorMessage] = useState('')
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInFormData>({
    defaultValues,
  })
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const setRoles = (roles: { name: string }[]) => {
    dispatch({
      type: UserActionTypes.SET_USER_ROLES,
      isAdmin: roles.map(item => item.name).includes('admin'),
      roles: roles.map(role => role.name),
    })
  }

  const onLoginCompleted = (data: ILoginData) => {
    if (data?.login.status) {
      const { accessToken, refreshToken } = data.login
      localStorage.setItem('accessToken', accessToken)
      localStorage.setItem('refreshToken', refreshToken)

      if (checkSignInRoles(data?.login.user.roles.map(item => item.name))) {
        setRoles(data?.login.user.roles)
        !!prevRoute && prevRoute !== '/login'
          ? navigate(prevRoute)
          : navigate('/admin')
      } else setErrorMessage(t('code:1028'))
    } else setErrorMessage(t(`code:${data.login.code}`))
  }

  const [loginUser, { loading }] = useLazyQuery<ILoginData, ILoginVars>(
    LOGIN_QUERY,
    {
      onCompleted: onLoginCompleted,
      onError: (error: ApolloError) => setErrorMessage(error.message),
      fetchPolicy: 'no-cache',
    },
  )

  const handleLogin = (formData: SignInFormData) => {
    const login = formData.login.trim()
    setErrorMessage('')
    loginUser({
      variables: {
        login,
        password: formData.password,
        app: `${window.TAG || 'local'}`,
        api_version: 36,
      },
    })
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: '20px 16px', md: '40px' },
        background: colors.bg,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-180px',
          left: '-120px',
          width: 520,
          height: 520,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(19,54,73,0.10) 0%, rgba(19,54,73,0) 70%)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-200px',
          right: '-140px',
          width: 560,
          height: 560,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(237,189,1,0.14) 0%, rgba(237,189,1,0) 70%)',
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 980,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr' },
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: colors.radiusHero,
          boxShadow: '0 34px 74px -44px rgba(19,54,73,.55)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: { xs: '20px', md: '34px' },
            padding: { xs: '30px 28px', md: '44px 40px' },
            background: `linear-gradient(120deg,${colors.navyHero} 0%,${colors.navyDark} 58%)`,
            color: '#fff',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '-110px',
              left: '-80px',
              width: 300,
              height: 300,
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(237,189,1,0.30) 0%, rgba(237,189,1,0) 70%)',
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '-130px',
              right: '-90px',
              width: 320,
              height: 320,
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(143,212,155,0.26) 0%, rgba(143,212,155,0) 70%)',
              pointerEvents: 'none',
            }}
          />

          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <LogoCircle size={44} />
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: 400,
                letterSpacing: '-0.01em',
              }}
            >
              {'My '}
              <Box component="span" sx={{ fontWeight: 800 }}>
                Olivia
              </Box>
            </Typography>
          </Box>

          <Box sx={{ position: 'relative' }}>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: colors.greenMint,
                marginBottom: '12px',
              }}
            >
              {t('login_screen:hero_kicker')}
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: '26px', md: '34px' },
                fontWeight: 800,
                letterSpacing: '-0.02em',
                lineHeight: 1.14,
                margin: 0,
              }}
            >
              {t('login_screen:hero_title')}
            </Typography>
            <Typography
              sx={{
                marginTop: '12px',
                fontSize: '14px',
                lineHeight: 1.6,
                color: 'rgba(255,255,255,0.62)',
                maxWidth: 330,
              }}
            >
              {t('login_screen:hero_subtitle')}
            </Typography>
          </Box>

          <Box
            sx={{
              position: 'relative',
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <HeroFeature label={t('login_screen:feature_parking')} />
            <HeroFeature label={t('login_screen:feature_users')} />
            <HeroFeature label={t('login_screen:feature_access')} />
          </Box>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit(handleLogin)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '18px',
            padding: { xs: '32px 26px 36px', md: '48px 44px' },
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: colors.slate,
                marginBottom: '10px',
              }}
            >
              {t('login_screen:kicker')}
            </Typography>
            <Typography
              component="h2"
              sx={{
                margin: 0,
                fontSize: '24px',
                fontWeight: 800,
                letterSpacing: '-0.01em',
                color: colors.textBody,
              }}
            >
              {t('login_screen:title')}
            </Typography>
            <Typography
              sx={{
                marginTop: '8px',
                fontSize: '13.5px',
                lineHeight: 1.6,
                color: colors.textSecondary,
              }}
            >
              {t('login_screen:subtitle')}
            </Typography>
          </Box>

          <Box>
            <FieldLabel>{t('email_login')}</FieldLabel>
            <Controller
              control={control}
              name="login"
              rules={{ required: `${t('field_required')}` }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextField
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!errors.login}
                  fullWidth
                  autoComplete="email"
                  autoCapitalize="none"
                  placeholder={t('email_login')}
                  sx={fieldSx}
                />
              )}
            />
            {errors.login?.message && (
              <Typography
                sx={{
                  marginTop: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: colors.red,
                }}
              >
                {errors.login.message}
              </Typography>
            )}
          </Box>

          <Box>
            <FieldLabel>{t('password')}</FieldLabel>
            <Controller
              control={control}
              name="password"
              rules={{ required: `${t('field_required')}` }}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextField
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!errors.password}
                  fullWidth
                  type={isPasswordVisible ? 'text' : 'password'}
                  autoComplete="current-password"
                  autoCapitalize="none"
                  placeholder={t('password')}
                  sx={fieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() =>
                            setIsPasswordVisible(!isPasswordVisible)
                          }
                          onMouseDown={event => event.preventDefault()}
                          edge="end"
                          sx={{ color: colors.slate, marginRight: '2px' }}
                        >
                          {isPasswordVisible ? (
                            <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                          ) : (
                            <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            {errors.password?.message && (
              <Typography
                sx={{
                  marginTop: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: colors.red,
                }}
              >
                {errors.password.message}
              </Typography>
            )}
          </Box>

          {!!errorMessage && (
            <Box
              data-e2e="login-error"
              sx={{
                display: 'flex',
                gap: '10px',
                padding: '12px 14px',
                borderRadius: '13px',
                background: colors.redBg,
                border: `1px solid ${colors.redBorder}`,
              }}
            >
              <ErrorOutlineIcon
                sx={{ fontSize: 18, color: colors.red, marginTop: '1px' }}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    lineHeight: 1.45,
                    color: colors.red,
                  }}
                >
                  {errorMessage}
                </Typography>
                <Typography
                  sx={{
                    marginTop: '2px',
                    fontSize: '11px',
                    color: colors.textMuted,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {alertTimestamp().trim()}
                </Typography>
              </Box>
            </Box>
          )}

          <Button
            type="submit"
            fullWidth
            disableElevation
            disabled={loading}
            sx={{
              marginTop: '4px',
              height: 50,
              borderRadius: '13px',
              textTransform: 'none',
              fontSize: '15px',
              fontWeight: 700,
              color: '#fff',
              background: `linear-gradient(135deg,${colors.navyHero} 0%,${colors.navy} 55%)`,
              boxShadow: colors.shadowBtn,
              '&:hover': {
                background: `linear-gradient(135deg,${colors.navy} 0%,${colors.navyDark} 55%)`,
                boxShadow: colors.shadowBtn,
              },
              '&.Mui-disabled': {
                background: colors.slate,
                color: 'rgba(255,255,255,0.8)',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: '#fff' }} />
            ) : (
              t('btn:log_in')
            )}
          </Button>
        </Box>
      </Box>

      <AppVersionLabel
        sx={{ position: 'fixed', left: '20px', bottom: '16px', zIndex: 1 }}
      />
    </Box>
  )
}

export default Login
